import { defaultTests } from "context/TestDataContext";

// Section labels are translation keys (defined in src/i18n/translations.js).
// `label` is kept as a stable FR identifier — it's used as a key in stored
// `profile.sectionTests` (localStorage) so it must not change between languages.
// Use resolveSectionLabel(section, t) at render time for the displayed label.
export const testSections = [
  {
    label: "Attention",
    labelKey: "section.attention",
    keys: [
      "TAP-Alerte", "TAP-GoNogo", "TAP-AttentionDivisée",
      "TAP-M-Alerte", "TAP-M-GoNogo", "TAP-M-Distractibilité", "TAP-M-AttentionDivisée",
      "TMT A (CERAD, 2005 / GREFEX, 2008)",
      "Color Trail 1",
      "Symboles (WAIS-IV)", "Symboles (WAIS-III)",
      "Code (WAIS-IV)", "Code (WAIS-III)",
    ],
  },
  {
    label: "Fonctions exécutives",
    labelKey: "section.executive",
    keys: [
      "Mémoire des chiffres (WAIS-IV)", "Mémoire des chiffres (WAIS-III)", "Empan inverse (WMS-R)",
      "Test du Labyrinthe (LT-G)", "Test de l'horloge", "Tâche des Apprentis",
      "TMT B (CERAD, 2005 / GREFEX, 2008)",
      "Fluences Animaux (CERAD, 2005)", "Fluences Mots M (CERAD, 2005)",
      "Color Trail 2",
      "Test de Stroop, partie 3 (D-KEFS, 2001)", "Test de Stroop, partie 4 (D-KEFS, 2001)",
      "Victoria Stroop, partie B (2009)", "Victoria Stroop, partie C (2009)",
      "BREF", "Frises",
    ],
  },
  {
    label: "Orientation",
    labelKey: "section.orientation",
    keys: [
      "Orientation temporelle", "Orientation spatiale",
      "Orientation personnelle", "Orientation circonstancielle",
    ],
  },
  {
    label: "Mémoire verbale",
    labelKey: "section.verbalMemory",
    keys: [
      "Mémoire des chiffres MDC (WAIS-IV)", "Mémoire des chiffres MDC (WAIS-III)",
      "10 mots (CERAD, 2005)", "10 mots (CERAD, 2005) (Row 48)", "10 mots (CERAD, 2005) (Row 49)",
      "RL/RI-16 (Van der Linden)",
      "RL/RI-16 (Amieva, 2007)",
      "GERIA-12 rappels libres", "GERIA-12 rappels indicés",
      "GERIA-12 rappel différé libre", "GERIA-12 rappel différé indicé",
      "GERIA-12 reconnaissance différée",
      "Textgedächtnis unmittelbar (WMS-IV)",
      "Textgedächtnis mittelfristig (WMS-IV)",
      "Textinhalte wiedererkennen (WMS-IV)",
      "5 mots (Dubois)", "Bildertest (NAI)",
      "Récit BEM-144 rappel immédiat", "Récit BEM-144 rappel différé",
      "Récit PeCPA-2r immédiat", "Récit PeCPA-2r différé",
    ],
  },
  {
    label: "Mémoire visuelle",
    labelKey: "section.visualMemory",
    keys: [
      "Empan direct (WMS-R)",
      "RCFT mémoire immédiat (Rey)", "RCFT mémoire différé (Rey)",
      "Figure de Rey simplifiée (rappel)",
      "Rappel de figures (CERAD, 2005)",
      "Portes (Doors and People)", "5 dessins (Croisile)",
    ],
  },
  {
    label: "Traitement visuo-spatial et praxies constructives",
    labelKey: "section.visuospatial",
    keys: [
      "TAP-ChampVisuel", "TAP-Négligence", "TAP-BalayageVisuel", "TAP-M-BalayageVisuel",
      "Bissection de lignes (Ferber)", "Apples Test (Humphreys)", "Bells Test (Rousseaux)",
      "RCFT copie (Rey)",
      "Figure de Rey simplifiée (copie)",
      "Copie de figures (CERAD, 2005)",
      "VOSP", "Appariement formes géométriques (TGV)",
      "Poppelreuter", "Maxi-Blatt", "Bildbeschreibung (Schneeball/Blinde Kuh)",
      "Figures superposées", "Identification de dessins au trait",
      "Reconnaissance de visages célèbres",
      "Description d'images", "Description de l'image de la gare",
    ],
  },
  {
    label: "Praxies gestuelles",
    labelKey: "section.praxies",
    keys: [
      "Production de gestes symboliques",
      "Production de pantomimes d'utilisation",
      "Imitation de gestes sans signification",
    ],
  },
  {
    label: "Langage oral et écrit",
    labelKey: "section.language",
    keys: [
      "Parole", "Discours spontané", "Compréhension orale",
      "Test de Stroop, partie 2 (D-KEFS, 2001)", "Test de Stroop, partie 1 (D-KEFS, 2001)", "Victoria Stroop, partie A (2009)",
      "Boston Naming Test (CERAD, 2005)",
      "Dénomination de visages célèbres", "DTLA (2017)",
      "Lecture à haute voix", "Écriture automatique (nom, prénom, adresse)", "Écriture spontanée",
    ],
  },
  {
    label: "Calcul",
    labelKey: "section.calcul",
    keys: ["Screening du calcul"],
  },
  {
    label: "Performances intellectuelles générales",
    labelKey: "section.cognitiveGlobal",
    keys: ["MMSE (CERAD, 2005)", "MoCA", "NOSGER (Spiegel et al., 1999)"],
  },
  {
    label: "Questionnaires",
    labelKey: "section.questionnaires",
    keys: [
      "DIA-S (Heidenblut & Zank, 2010)",
      "Symptomatologie dépressive (Mini-GDS)", "Symptomatologie anxieuse (GAI-FC-SF)",
      "HADS-D (Herrmann et al., 2011)",
      "Symptomatologie anxieuse (GAD-7)", "Symptomatologie dépressive (NDDI-E)",
      "Somnolence (Epworth)", "Autonomie en vie quotidienne (IADL)",
      "Fonctionnement cognitif global (IQCODE)", "Autonomie en vie quotidienne (CDR)",
      "BDQ (Rascovsky et al., 2011)",
    ],
  },
];

const allSectionedKeys = new Set(testSections.flatMap((s) => s.keys));
const unsectionedKeys = Object.keys(defaultTests).filter((k) => !allSectionedKeys.has(k));
if (unsectionedKeys.length > 0) {
  testSections.push({ label: "Autres", labelKey: "section.other", keys: unsectionedKeys });
}

const keyCounts = {};
for (const s of testSections) {
  for (const k of s.keys) keyCounts[k] = (keyCounts[k] || 0) + 1;
}
export const multiSectionKeys = Object.keys(keyCounts).filter((k) => keyCounts[k] > 1);

// Resolve a section's display label using a translation function (from useTranslation()).
export const resolveSectionLabel = (section, t) => t(section.labelKey);

// Returns the FR-label identifiers for the sections containing `key`. These are
// the stable keys stored in profile.sectionTests in localStorage.
export const sectionsForKey = (key) =>
  testSections.filter((s) => s.keys.includes(key)).map((s) => s.label);
